import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CheckCircle2, Copy, Eye, Loader2, RefreshCw, Wallet, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmModal } from '@/components/common';
import { adminService } from '@/services';
import type { BankDetails, Payment } from '@/types';
import {
  extractErrorMessage,
  formatEmployeeId,
  formatNaira,
  showErrorToast,
  showSuccessToast,
} from '@/utils';
import { cn } from '@/utils/cn';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function Payments() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [selectedBankDetails, setSelectedBankDetails] = useState<BankDetails | null>(null);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const data = await adminService.getPaymentsByMonth(selectedYear, selectedMonth);
      setPayments(data);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [selectedYear, selectedMonth]);

  const handleMarkAsPaid = async () => {
    if (!pendingPaymentId) return;

    setMarkingPaid(pendingPaymentId);
    try {
      await adminService.markPaymentAsPaid(pendingPaymentId);
      showSuccessToast('Payment marked as paid successfully');
      setConfirmModalOpen(false);
      setPendingPaymentId(null);
      await fetchPayments();
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to mark payment as paid'));
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleViewBank = (bankDetails: BankDetails | undefined) => {
    if (bankDetails) {
      setSelectedBankDetails(bankDetails);
      setBankModalOpen(true);
    } else {
      showErrorToast('Bank details not available for this payment');
    }
  };

  const handleRecomputePayment = async (paymentId: string) => {
    setRecomputing(paymentId);
    try {
      await adminService.recomputePayment(paymentId);
      showSuccessToast('Payment recomputed successfully with updated salary/allowance');
      await fetchPayments();
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to recompute payment'));
    } finally {
      setRecomputing(null);
    }
  };

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${fieldName} copied to clipboard`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getAvailableMonths = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const months: Array<{ year: number; month: number; label: string }> = [];

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      months.push({
        year,
        month,
        label: `${MONTHS[month - 1]} ${year}`,
      });
    }

    return months;
  };

  const availableMonths = getAvailableMonths();

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Payments
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Manage and track staff payments for each month
          </p>
        </motion.div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split('-').map(Number);
                setSelectedYear(year);
                setSelectedMonth(month);
              }}
              className="px-4 py-2 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 text-sm font-black text-slate-900 dark:text-white focus:ring-2 focus:ring-neolab-green/20 outline-none"
            >
              {availableMonths.map((m) => (
                <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 text-neolab-green animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Employee
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Base Salary
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Allowance
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Normal Pay
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Normal Late
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Extra Late
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Missed Out
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Missed In
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Total Deduct
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Net Income
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">
                    Status
                  </th>
                  <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                          <Wallet className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        </div>
                        <p className="text-slate-500 dark:text-slate-400 font-bold">
                          No payment records found for this month
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => (
                    <tr
                      key={payment._id}
                      className="border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="p-2">
                        <div>
                          <p className="text-xs font-black text-slate-900 dark:text-white">
                            {payment.userName}
                          </p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                            {formatEmployeeId(payment.employeeId)}
                          </p>
                        </div>
                      </td>
                      <td className="p-2 font-black text-[11px] text-slate-900 dark:text-white">
                        {formatNaira(payment.baseSalary)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-slate-900 dark:text-white">
                        {formatNaira(payment.allowance)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-emerald-600 dark:text-emerald-400">
                        {formatNaira(payment.normalMonthlyPay)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-amber-600 dark:text-amber-400">
                        {formatNaira(payment.normalLatenessDeductions)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-orange-600 dark:text-orange-400">
                        {formatNaira(payment.extraLatenessDeductions)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-rose-600 dark:text-rose-400">
                        {formatNaira(payment.missedClockOutDeductions)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-purple-600 dark:text-purple-400">
                        {formatNaira(payment.missedClockInDeductions)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-rose-600 dark:text-rose-400">
                        {formatNaira(payment.totalDeductions)}
                      </td>
                      <td className="p-2 font-black text-[11px] text-slate-900 dark:text-white">
                        {formatNaira(payment.netIncome)}
                      </td>
                      <td className="p-2 text-center">
                        {payment.paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-wider">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase tracking-wider">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex justify-center items-center gap-1.5">
                          {payment.bankDetails && (
                            <button
                              type="button"
                              onClick={() => handleViewBank(payment.bankDetails)}
                              className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-all cursor-pointer"
                              title="View Bank Details"
                            >
                              <Eye className="w-3 h-3" />
                            </button>
                          )}
                          {!payment.paid && (
                            <button
                              type="button"
                              onClick={() => handleRecomputePayment(payment._id)}
                              disabled={recomputing === payment._id}
                              className={cn(
                                'p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition-all cursor-pointer',
                                recomputing === payment._id && 'opacity-50 cursor-not-allowed',
                              )}
                              title="Recompute payment with updated salary/allowance"
                            >
                              {recomputing === payment._id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                            </button>
                          )}
                          {!payment.paid ? (
                            <button
                              type="button"
                              onClick={() => {
                                setPendingPaymentId(payment._id);
                                setConfirmModalOpen(true);
                              }}
                              disabled={markingPaid === payment._id}
                              className={cn(
                                'px-2 py-1 rounded-lg bg-neolab-green text-white font-black text-[10px] shadow-lg shadow-green-500/20 hover:bg-neolab-green/90 transition-all cursor-pointer',
                                markingPaid === payment._id && 'opacity-50 cursor-not-allowed',
                              )}
                            >
                              {markingPaid === payment._id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Pay'
                              )}
                            </button>
                          ) : (
                            <span className="text-[10px] font-black text-slate-400">
                              {payment.paidAt
                                ? new Date(payment.paidAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })
                                : 'Paid'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setPendingPaymentId(null);
        }}
        onConfirm={handleMarkAsPaid}
        title="Mark Payment as Paid"
        message="Are you sure you want to mark this payment as paid? This action cannot be undone."
        confirmLabel="Mark as Paid"
        variant="success"
        isLoading={markingPaid !== null}
      />

      <AnimatePresence>
        {bankModalOpen && selectedBankDetails && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setBankModalOpen(false);
                setSelectedBankDetails(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-8 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  Bank Details
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setBankModalOpen(false);
                    setSelectedBankDetails(null);
                  }}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="bank-account-name"
                    className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-2 block"
                  >
                    Account Name
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="bank-account-name"
                      type="text"
                      value={selectedBankDetails.accountName}
                      readOnly
                      className="flex-1 px-4 py-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none text-sm font-black text-slate-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyToClipboard(selectedBankDetails.accountName, 'Account name')
                      }
                      className="p-2 rounded-xl bg-neolab-green text-white hover:bg-neolab-green/90 transition-all cursor-pointer"
                      title="Copy account name"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="bank-account-number"
                    className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-2 block"
                  >
                    Account Number
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="bank-account-number"
                      type="text"
                      value={selectedBankDetails.accountNumber}
                      readOnly
                      className="flex-1 px-4 py-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none text-sm font-black text-slate-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyToClipboard(selectedBankDetails.accountNumber, 'Account number')
                      }
                      className="p-2 rounded-xl bg-neolab-green text-white hover:bg-neolab-green/90 transition-all cursor-pointer"
                      title="Copy account number"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="bank-name"
                    className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-2 block"
                  >
                    Bank Name
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="bank-name"
                      type="text"
                      value={selectedBankDetails.bankName}
                      readOnly
                      className="flex-1 px-4 py-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none text-sm font-black text-slate-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyToClipboard(selectedBankDetails.bankName, 'Bank name')
                      }
                      className="p-2 rounded-xl bg-neolab-green text-white hover:bg-neolab-green/90 transition-all cursor-pointer"
                      title="Copy bank name"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Payments;
